import { InsuranceGroup, InsuranceCategory, JobStatus, CustomDimensions, UserProfile, LegacyInterest, LegacyTrackingAction } from './types';
import { pageSettings } from './page-settings';
import { getAgeFromDoB, getYearOfBirth } from '../date';

const GROUP_INTEREST_MAP: { [key in InsuranceGroup | 'na']: LegacyTrackingAction } = {
  student: 'comprehensive.students',
  'civil-servant': 'civil-servant',
  'civil-servant-applicant': 'civil-servant',
  'employee_self-employed': 'comprehensive',
  expat: 'comprehensive-expat',
  clinic: 'clinic',
  dental: 'dental',
  collective: 'all.all',
  na: 'all.all'
};

export function getPageDimensions() {
  return {
    page_insurance_category: pageSettings.insuranceCategory || 'na',
    page_insurance_group: pageSettings.insuranceGroup || 'na'
  };
}
export function getPhoneLeadDimensions(userProfile?: UserProfile) {
  const dimensions = getLeadDimensions(userProfile, { includeProduct: true });
  dimensions.job_status ||= (window as any).craftConfig?.jobStatus ?? 'na';
  return { ...dimensions };
}

export function getEmailLeadDimenions(userProfile?: UserProfile) {
  return getLeadDimensions(userProfile, { includeProduct: true });
}

export function getLeadDimensions(userProfile?: UserProfile, options: { includeProduct?: boolean } = {}): CustomDimensions {
  const { date_of_birth, employment, gender, nationality, visa, jaeg_reached, income, customer_type } = userProfile || {};

  let insurance_group: InsuranceGroup | undefined;
  let insurance_category: InsuranceCategory | undefined = userProfile?.insurance_category;

  switch (employment) {
    case 'civil-servant':
    case 'civil-servant-applicant':
      if (insurance_category === 'full') {
        insurance_category = 'residual';
      }
      break;
    case 'employed':
    case 'self-employed':
    case 'student':
      if (insurance_category === 'residual') {
        insurance_category = 'full';
      }
      break;
    default:
      break;
  }

  if (insurance_category) {
    insurance_group = getGroup(pageSettings.isExpat, insurance_category, employment || pageSettings.jobStatus);
    if (!insurance_group && options.includeProduct) {
      insurance_group = pageSettings.insuranceGroup === 'expat' ? 'expat' : 'na';
    }
  } else if (employment) {
    insurance_group = getGroup(pageSettings.isExpat, pageSettings.insuranceCategory, employment);
  } else if (options.includeProduct) {
    insurance_category = pageSettings.insuranceCategory || 'na';
    insurance_group = pageSettings.insuranceGroup || 'na';
  }

  //@ts-ignore visa status is just Visa as lower case
  const visa_status: 'limited' | 'unlimited' = visa?.toLowerCase() || 'unlimited';

  return {
    ...(insurance_category && { insurance_category }),
    ...(insurance_group && { insurance_group }),
    ...(date_of_birth && {
      age: getAgeFromDoB(date_of_birth),
      yob: getYearOfBirth(date_of_birth)
    }),
    ...(income && { income: parseIncome(income) }),
    ...(jaeg_reached !== undefined && { jaeg_reached }),
    ...(employment && { job_status: employment }),
    ...(nationality && { nationality: nationality.toLowerCase() }),
    ...(visa && { visa_status }),
    ...(gender && { gender }),
    ...(customer_type && { customer_type })
  };
}
export function getCategoryFromInterest(interest: LegacyInterest): InsuranceCategory | undefined {
  switch (interest) {
    case 'civil-servant':
    case 'civil-servant-applicant':
      return 'residual';
    case 'comprehensive':
    case 'comprehensive-student':
    case 'comprehensive-expat':
      return 'full';
    case 'general':
      return 'na';
    case 'dental':
    case 'clinic':
    case 'collective':
      return interest;
    default:
      return;
  }
}

export function getCategoryFromGroup(insuranceGroup: InsuranceGroup): InsuranceCategory {
  switch (insuranceGroup) {
    case 'employee_self-employed':
    case 'student':
    case 'expat':
      return 'full';
    case 'civil-servant':
    case 'civil-servant-applicant':
      return 'residual';
    case 'dental':
      return 'dental';
    case 'clinic':
      return 'clinic';
    default:
      return 'na';
  }
}

export function getLegacyTrackingAction(userProfile?: UserProfile): LegacyTrackingAction {
  const insurance_group = getGroup(pageSettings.isExpat, userProfile?.insurance_category || pageSettings.insuranceCategory, userProfile?.employment || pageSettings.jobStatus);

  return GROUP_INTEREST_MAP[insurance_group || 'na'];
}

function getGroup(isExpat: boolean, category?: InsuranceCategory, jobStatus?: JobStatus): InsuranceGroup | undefined {
  switch (category) {
    case 'full':
      switch (jobStatus) {
        case 'employed':
        case 'self-employed':
        case 'trainee':
          return isExpat ? 'expat' : 'employee_self-employed';
        case 'student':
          return 'student';
        default:
          return;
      }
    case 'residual':
      switch (jobStatus) {
        case 'civil-servant':
          return 'civil-servant';
        case 'civil-servant-applicant':
          return 'civil-servant-applicant';
        default:
          return;
      }
    case 'collective':
      return 'collective';
    case 'dental':
      return 'dental';
    case 'clinic':
      return 'clinic';
    default:
      return;
  }
}
export function getGroupFromFormdata(formdata: { insurance_category?: InsuranceCategory; employment?: JobStatus }) {
  const { insurance_category, employment } = formdata;
  return getGroup(pageSettings.isExpat, insurance_category, employment);
}

function parseIncome(income: string | number) {
  const parsedIncome = parseInt(income.toString().replace('.', ''));
  if (isNaN(parsedIncome)) {
    return 'na';
  }
  return parsedIncome.toString();
}
